import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { useModeratorsCompaniesSourceBreakdownRetrieve } from "@/services/moderators/generated/moderators-companies/moderators-companies";
import "./ModeratorPage.css";
import "./LeadSources.css";

/** Filter values for the moderator companies list (`sourceType`). */
const SOURCE_TYPES = ["institution", "event", "partner", "other", "unknown"] as const;

/** Source-type filter options, translated at call (render) time. */
export function sourceTypeFilters(): { value: (typeof SOURCE_TYPES)[number]; label: string }[] {
  return SOURCE_TYPES.map((value) => ({ value, label: i18n.t(`deals:sourceBreakdown.sourceType.${value}`) }));
}

interface SourceBreakdownCardProps {
  onPickType?: (type: string) => void;
  onPickSource?: (key: string) => void;
}

/**
 * Where companies came from (WP1.9): counts by source type and by named
 * source, including companies registered before sources were recorded
 * ("Unknown"). Clicking a row filters the companies list.
 */
const SourceBreakdownCard = ({ onPickType, onPickSource }: SourceBreakdownCardProps) => {
  const { t } = useTranslation(["deals", "common"]);
  const { data, isLoading, isError } = useModeratorsCompaniesSourceBreakdownRetrieve({
    query: { refetchOnMount: true },
  });

  const cell = (label: string, onClick?: () => void) =>
    onClick ? (
      <button type="button" className="mod-sources__link" onClick={onClick}>
        {label}
      </button>
    ) : (
      label
    );

  return (
    <section className="mod-page__card" aria-labelledby="source-breakdown-title">
      <div className="mod-page__card-body">
        <h2 id="source-breakdown-title" className="mod-page__card-title">
          {data?.total !== undefined
            ? t("sourceBreakdown.titleWithTotal", { total: data.total })
            : t("sourceBreakdown.title")}
        </h2>
        {isLoading ? <p className="mod-page__card-meta">{t("common:states.loading")}</p> : null}
        {isError ? <p className="mod-page__card-meta">{t("sourceBreakdown.loadError")}</p> : null}
        {data ? (
          <div className="mod-sources__grid">
            <div className="mod-sources__table-wrap">
              <table className="mod-sources__table">
                <thead>
                  <tr>
                    <th>{t("sourceBreakdown.type")}</th>
                    <th className="mod-sources__count">{t("sourceBreakdown.companies")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.byType ?? []).map((row) => (
                    <tr key={row.type}>
                      <td>
                        {cell(
                          row.label ?? row.type ?? "",
                          onPickType && row.type ? () => onPickType(row.type ?? "") : undefined,
                        )}
                      </td>
                      <td className="mod-sources__count">{row.count ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mod-sources__table-wrap">
              <table className="mod-sources__table">
                <thead>
                  <tr>
                    <th>{t("sourceBreakdown.namedSource")}</th>
                    <th className="mod-sources__count">{t("sourceBreakdown.companies")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.bySource ?? []).map((row) => (
                    <tr key={row.key}>
                      <td>
                        {cell(
                          row.isActive === false
                            ? t("sourceBreakdown.inactive", { name: row.name ?? "" })
                            : (row.name ?? ""),
                          onPickSource && row.key ? () => onPickSource(row.key ?? "") : undefined,
                        )}
                      </td>
                      <td className="mod-sources__count">{row.count ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default SourceBreakdownCard;
